import React from "react";
import { Cabinet } from "@/components/Cabinet";
import { Starfield } from "@/components/Starfield";
import { LEVELS, MAX_VISIBLE_LEVEL } from "@/game/gameData";
import { computeNextChar } from "@/game/logic";
import { useGameLoop } from "@/game/useGameLoop";
import { LevelComplete, GameOver, LevelIntro, PracticeSummary } from "@/components/Overlays";
import { FingerKeyboard } from "@/components/FingerKeyboard";
import { PixelHands } from "@/components/PixelHands";
import { Alien, BossSprite, BossHud, Hud, InputLine, Explosion } from "@/components/GameBits";

function Battlefield({ s, level, norm, flashOpacity }) {
  const inBoss = s.phase === "boss" && s.boss;
  return (
    <div className="relative flex-1 overflow-hidden">
      <Starfield />
      {inBoss && <BossHud boss={s.boss} kind={level.boss} />}
      {inBoss && <BossSprite boss={s.boss} kind={level.boss} typed={s.typed} norm={norm} />}
      {s.aliens.map((a) => (
        <Alien key={a.id} a={a} typed={s.typed} norm={norm} />
      ))}
      {s.effects.map((e) => (
        <Explosion key={e.id} x={e.x} y={e.y} color={e.color} text={e.text} />
      ))}
      {flashOpacity > 0 && (
        <div className="absolute inset-0 z-40 pointer-events-none" style={{ background: s.flashColor, opacity: flashOpacity }} />
      )}
    </div>
  );
}

function EndOverlays({ s, level, result, practice, restart, startWave, goHome, goNext }) {
  if (s.phase === "intro" && !result) return <LevelIntro level={level} practice={practice} onStart={startWave} />;
  if (result?.type === "practice") return <PracticeSummary result={result} onReplay={restart} onHome={goHome} />;
  if (result?.type === "complete") {
    return <LevelComplete result={result} hasNext={level.id < MAX_VISIBLE_LEVEL} onNext={goNext} onReplay={restart} onHome={goHome} />;
  }
  if (result?.type === "over") return <GameOver result={result} onRetry={restart} onHome={goHome} />;
  return null;
}

export function GameScreen({ player, setPlayer, levelId, onExit, onNext, settings, practice = false }) {
  const level = LEVELS.find((l) => l.id === levelId);
  const { s, norm, result, restart, startWave, finishOrExit } = useGameLoop({
    level,
    practice,
    player,
    setPlayer,
    onExit,
    soundEnabledRef: settings.soundEnabledRef,
  });

  const now = performance.now();
  const shaking = now < s.shakeUntil;
  const flashOpacity = Math.max(0, (s.flashUntil - now) / 300) * 0.5;
  const nextChar = computeNextChar(s, norm);
  const goHome = () => onExit(player);
  const goNext = () => onNext(level.id + 1);

  return (
    <Cabinet settings={settings} title={`TYPEBLASTER · LVL ${level.id}`}>
      <div className={`relative z-10 h-full flex flex-col ${shaking ? "tb-shake" : ""}`}>
        <Hud s={s} level={level} practice={practice} onFinishPractice={finishOrExit} />

        <div className="text-center py-1 bg-[#0b0b18]/80 border-b border-[#1e1c3b] z-20">
          <span className="font-vt text-lg text-emerald-300">{level.hint}</span>
        </div>

        <Battlefield s={s} level={level} norm={norm} flashOpacity={flashOpacity} />

        {settings.showKeyboard && s.phase !== "intro" && (
          <div
            className="relative z-30 flex items-end justify-center gap-4 py-1.5 bg-[#0b0b18]/90 border-t border-[#2f2c5a]"
            data-testid="finger-guide-panel"
          >
            <PixelHands nextChar={nextChar} size={56}>
              <FingerKeyboard nextChar={nextChar} compact />
            </PixelHands>
          </div>
        )}

        <InputLine typed={s.typed} />
      </div>

      <EndOverlays s={s} level={level} result={result} practice={practice} restart={restart} startWave={startWave} goHome={goHome} goNext={goNext} />
    </Cabinet>
  );
}
